import React, { useCallback, useEffect, useRef } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import normalize from 'common/Normalize';
import Text from 'design-system/Text';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { isoCodeToEmojiFlag } from 'utils/formatting';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { PhoneNumberUtil } from 'google-libphonenumber';
import prefixes from 'login/sms/prefixes';
import IconArrowTriangle from './icons/IconArrowTriangle';
import { rem } from './values';
const phoneUtil = PhoneNumberUtil.getInstance();
const PhoneNumberInputView = ({ value, onOpenPrefix, onSelectedPrefix, textInputRef, code, prefix, onSubmitEditing, onChangeText, placeholder, }) => {
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const timeoutRef = useRef(undefined);
    const onPickCancel = useCallback(() => {
        timeoutRef.current = setTimeout(() => {
            textInputRef?.current?.focus();
        });
    }, [textInputRef]);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const selectedPrefixManually = useRef(false);
    const openPrefixes = useCallback(() => {
        if (onOpenPrefix) {
            onOpenPrefix();
        }
        navigation.navigateDeprecated('PhonePrefixes', {
            onSelectedPrefix: (country) => {
                selectedPrefixManually.current = true;
                onSelectedPrefix(country);
            },
            onPickCancel,
        });
    }, [onOpenPrefix, onPickCancel, onSelectedPrefix]);
    useAutoFocusInput(textInputRef);
    const styles = useStyles(styleSet);
    const textChanged = useCallback((raw) => {
        if (raw && raw.length) {
            try {
                const number = phoneUtil.parse(raw, code);
                const numberCountryCode = number.getCountryCode();
                // only do this if the user hasnt manually selected
                if (!selectedPrefixManually.current) {
                    if (numberCountryCode === 44) {
                        const country = prefixes['United Kingdom'];
                        onSelectedPrefix({ country: country.iso2, item: country });
                    }
                    else if (numberCountryCode === 1) {
                        const country = prefixes['United States of America'];
                        onSelectedPrefix({ country: country.iso2, item: country });
                    }
                    else {
                        const country = Object.values(prefixes).find((p) => p.prefix.substring(1) === number.getCountryCode()?.toFixed());
                        if (country) {
                            onSelectedPrefix({ country: country.iso2, item: country });
                        }
                    }
                }
                if (phoneUtil.isValidNumber(number)) {
                    const nationalNumber = number.getNationalNumber()?.toFixed(0);
                    if (nationalNumber) {
                        onChangeText(nationalNumber);
                        return;
                    }
                }
            }
            catch {
                // noop
            }
        }
        onChangeText(raw);
    }, [code, onChangeText, onSelectedPrefix]);
    return (<View style={styles.row} testID="phoneNumberInput">
      <TouchableOpacity onPress={openPrefixes} style={styles.prefixContainer}>
        <Text Numbers-20 Secondary>
          {isoCodeToEmojiFlag(code)} {prefix}
        </Text>
        <View style={styles.arrow}>
          <IconArrowTriangle width={rem(10)} height={rem(8.3)} color={colors.elements.secondary}/>
        </View>
      </TouchableOpacity>
      <TextInput testID="textInput" ref={textInputRef} placeholder={placeholder !== undefined ? placeholder : 'Phone Number'} autoCorrect={false} allowFontScaling={false} keyboardType="phone-pad" maxLength={17} value={value || ''} onSubmitEditing={onSubmitEditing} autoComplete="tel" importantForAutofill="yes" onChangeText={textChanged} selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder} underlineColorAndroid="transparent" style={styles.textInput}/>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        marginHorizontal: rem(8),
    },
    prefixContainer: {
        alignSelf: 'center',
        padding: rem(8),
        borderRadius: rem(15),
        flexDirection: 'row',
        backgroundColor: colors.cards.onLight,
    },
    textInput: {
        marginLeft: rem(16),
        fontSize: normalize(22),
        fontFamily: 'Montserrat-Light',
        textAlign: 'left',
        textAlignVertical: 'center',
        flex: 1,
        color: colors.text.primary,
    },
    arrow: {
        justifyContent: 'center',
        marginLeft: rem(8),
    },
}));
export default PhoneNumberInputView;
